import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { CircleHelp, LayoutGrid, LogOut, Search, UserRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api.ts';
import { logout } from '../auth.tsx';
import { itemsOf, locate, type NavArea } from '../navigation.ts';
import { usePlatformLook } from '../platform.tsx';
import { AdminBadge } from './AdminBadge.tsx';

/**
 * Layout "Abas de trabalho" (ADR 0128): cada tela aberta vira uma aba, como num programa de
 * computador. O menu completo abre num painel grande pelo botão "Menu". A barra de baixo mostra
 * quem está usando e o atalho da busca.
 */

const TABS_KEY = 'app.openTabs';
const MAX_TABS = 8;

interface OpenTab {
  to: string;
  key: string;
}

function readTabs(): OpenTab[] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(TABS_KEY) ?? '[]') as unknown;
    return Array.isArray(raw)
      ? raw.filter((t): t is OpenTab => typeof t?.to === 'string' && typeof t?.key === 'string')
      : [];
  } catch {
    return [];
  }
}

function saveTabs(tabs: OpenTab[]) {
  try {
    sessionStorage.setItem(TABS_KEY, JSON.stringify(tabs));
  } catch {
    // navegador sem armazenamento: as abas só não sobrevivem a um recarregar
  }
}

/**
 * As abas abertas: a tela atual entra sozinha (a mais antiga sai quando passa do limite); o Início
 * fica sempre e não fecha.
 */
export function nextTabs(tabs: OpenTab[], current: OpenTab | null): OpenTab[] {
  if (!current || current.to === '/' || tabs.some((t) => t.to === current.to)) return tabs;
  return [...tabs, current].slice(-MAX_TABS);
}

function useOpenTabs(areas: NavArea[]) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [tabs, setTabs] = useState(readTabs);
  const { item, inner } = locate(areas, pathname);
  const key = inner?.key ?? item?.key;
  const current = key ? { to: pathname, key } : null;
  const next = nextTabs(tabs, current);
  if (next !== tabs) setTabs(next);
  useEffect(() => saveTabs(tabs), [tabs]);
  return { tabs, setTabs, pathname };
}

export function WorkTabsBar({
  areas,
  me,
  onSearch,
  statusBar,
}: {
  areas: NavArea[];
  me: Me;
  onSearch: () => void;
  statusBar: boolean;
}) {
  const { t } = useTranslation();
  const look = usePlatformLook();
  const navigate = useNavigate();
  const { tabs, setTabs, pathname } = useOpenTabs(areas);
  const [megaOpen, setMegaOpen] = useState(false);
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    if (megaOpen) setMegaOpen(false);
  }
  useEffect(() => {
    if (!megaOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMegaOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [megaOpen]);

  const close = (to: string) => {
    const rest = tabs.filter((tab) => tab.to !== to);
    setTabs(rest);
    if (to === pathname) void navigate({ to: rest[rest.length - 1]?.to ?? '/' });
  };
  const tabClass = (active: boolean) =>
    `flex h-8 max-w-60 shrink-0 items-center gap-2 rounded-t-md border border-b-0 px-3 text-xs ${
      active
        ? 'relative -mb-px h-9 border-rule bg-paper font-medium text-ink'
        : 'border-transparent text-muted hover:text-ink'
    }`;

  return (
    <>
      <div className="ui-sidebar relative z-20 hidden shrink-0 items-center gap-2 px-3 py-2 md:flex">
        <button
          type="button"
          onClick={() => setMegaOpen((o) => !o)}
          aria-expanded={megaOpen}
          className="flex min-h-9 items-center gap-2 rounded-md border border-control-border px-3 text-sm font-medium text-ink hover:bg-paper"
        >
          <LayoutGrid size={16} strokeWidth={1.75} aria-hidden />
          {t('nav.menu')}
        </button>
        <Link to="/" aria-label={t('nav.homeBrand')} className="flex shrink-0 items-center px-2">
          <img src={look.logoUrl} alt="" className="h-7 w-auto max-w-36 object-contain" />
        </Link>
        <AdminBadge />
        <button
          type="button"
          onClick={onSearch}
          className="mx-auto flex min-h-9 w-full max-w-md items-center justify-between gap-2 rounded-md border border-rule bg-paper px-3 text-left text-[13px] text-muted hover:border-control-border"
        >
          <span className="flex items-center gap-2">
            <Search size={14} aria-hidden />
            {t('nav.search')}
          </span>
          <kbd className="rounded border border-rule bg-sheet px-1.5 font-mono text-[11px]">
            Ctrl K
          </kbd>
        </button>
        <Link
          to="/account"
          className="flex min-h-9 max-w-48 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-ink hover:bg-paper"
        >
          <UserRound size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
          <span className="truncate">{me.user.displayName}</span>
        </Link>
        <Link
          to="/help"
          aria-label={t('nav.help')}
          title={t('nav.help')}
          className="flex size-9 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
        >
          <CircleHelp size={18} strokeWidth={1.75} aria-hidden />
        </Link>
        <button
          type="button"
          onClick={() => void logout()}
          className="flex min-h-9 items-center gap-1.5 rounded-md px-2 text-sm text-brand-text hover:underline"
        >
          <LogOut size={16} strokeWidth={1.75} aria-hidden />
          {t('auth.logout')}
        </button>
        {megaOpen && (
          <>
            <button
              type="button"
              aria-label={t('nav.close')}
              onClick={() => setMegaOpen(false)}
              className="fixed inset-0 z-10 cursor-default"
            />
            <nav
              aria-label={t('nav.label')}
              className="absolute top-full left-3 z-20 mt-1 grid w-[min(760px,calc(100vw-24px))] grid-cols-3 gap-6 rounded-lg border border-rule bg-sheet p-5 shadow-[0_12px_32px_rgb(23_32_43/0.16)]"
            >
              {areas.map((area) => {
                const Icon = area.icon;
                return (
                  <div key={area.id} className="flex flex-col gap-1">
                    <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <Icon size={16} strokeWidth={1.75} aria-hidden className="text-brand-text" />
                      {t(area.key)}
                    </p>
                    {itemsOf(area).map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="rounded px-6 py-1 text-sm text-muted hover:bg-paper hover:text-ink"
                      >
                        {t(item.key)}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </nav>
          </>
        )}
      </div>
      <nav
        aria-label={t('nav.openTabs')}
        className="hidden shrink-0 items-end gap-1 overflow-x-auto border-b border-rule bg-sheet px-3 pt-2 md:flex"
      >
        <Link
          to="/"
          aria-current={pathname === '/' ? 'page' : undefined}
          className={tabClass(pathname === '/')}
        >
          {t('nav.home')}
        </Link>
        {tabs.map((tab) => (
          <span key={tab.to} className={tabClass(tab.to === pathname)}>
            <Link
              to={tab.to}
              aria-current={tab.to === pathname ? 'page' : undefined}
              className="truncate"
            >
              {t(tab.key)}
            </Link>
            <button
              type="button"
              onClick={() => close(tab.to)}
              aria-label={t('nav.closeTab', { name: t(tab.key) })}
              className="flex size-5 shrink-0 items-center justify-center rounded text-muted hover:bg-paper hover:text-ink"
            >
              <X size={12} aria-hidden />
            </button>
          </span>
        ))}
      </nav>
      {statusBar && <StatusBarSlot appName={look.appName} me={me} />}
    </>
  );
}

/** A barra de baixo das abas de trabalho: o sistema, quem está usando e o atalho da busca. */
function StatusBarSlot({ appName, me }: { appName: string; me: Me }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 hidden h-7 items-center gap-5 border-t border-rule bg-sheet px-4 text-xs text-muted md:flex">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className="size-2 rounded-full bg-ok" />
        {t('nav.connected')}
      </span>
      <span>{appName}</span>
      <span>{me.user.displayName}</span>
      <span className="ml-auto">{t('nav.searchShortcut')}</span>
    </div>
  );
}
